import React, { useMemo } from 'react';
import { KanbanBoard, ColumnDefinition } from './KanbanBoard';
import InterviewCard from './InterviewCard';
import { categorizeInterviews } from '../../utils/interviewCategorization';
import { InterviewItem } from './types';
import { ATS_CONSTANTS } from './constants';
import styles from './KanbanColumn.module.css'; // Reuse column styles
import EmptyStateCard from './components/EmptyStateCard';
import { ActionNeededIcon, UpcomingIcon, CompletedIcon, NoShowIcon, CancelledIcon } from './components/EmptyStateIcons';

interface InterviewBoardProps {
  interviewItems: InterviewItem[];
  jobStatus: string;
  onCardClick: (candidate: InterviewItem) => void;
  onReschedule: (candidate: InterviewItem) => void;
  onCancelInterview: (candidate: InterviewItem) => void;
  onUpdateStatus: (candidate: InterviewItem, status: string) => void;

  onGenerateOffer: (candidate: InterviewItem) => void;
}

const InterviewBoard: React.FC<InterviewBoardProps> = ({
  interviewItems,
  jobStatus,
  onCardClick,
  onReschedule,
  onCancelInterview,
  onUpdateStatus,

  onGenerateOffer,
}) => {
  const isClosed = jobStatus === 'Closed' || jobStatus === 'Filled';

  const categorizedInterviews = useMemo(() => {
    if (!interviewItems || interviewItems.length === 0) {
      return {
        actionNeeded: [],
        upcoming: [],
        completed: [],
        cancelled: [],
        noShow: [],
      };
    }
    return categorizeInterviews(interviewItems);
  }, [interviewItems]);

  const columns: ColumnDefinition<InterviewItem>[] = useMemo(() => {
    return [
      {
        id: ATS_CONSTANTS.INTERVIEW_COLUMNS.ACTION_NEEDED,
        title: 'Action Needed',
        items: categorizedInterviews.actionNeeded,
        headerClassName: styles.kanbanColumnHeaderApplied, // Blue header
        emptyState: <EmptyStateCard message="No actions needed" icon={<ActionNeededIcon />} />,
      },
      {
        id: ATS_CONSTANTS.INTERVIEW_COLUMNS.UPCOMING,
        title: 'Upcoming',
        items: categorizedInterviews.upcoming,
        headerClassName: styles.kanbanColumnHeaderInConsideration, // Blue header
        emptyState: <EmptyStateCard message="No upcoming interviews" icon={<UpcomingIcon />} />,
      },
      {
        id: ATS_CONSTANTS.INTERVIEW_COLUMNS.COMPLETED,
        title: 'Completed',
        items: categorizedInterviews.completed,
        headerClassName: styles.kanbanColumnHeaderAccepted, // Green/Blue header
        emptyState: <EmptyStateCard message="No completed interviews yet" icon={<CompletedIcon />} />,
      },
      {
        id: ATS_CONSTANTS.INTERVIEW_COLUMNS.NO_SHOW,
        title: 'No Show',
        items: categorizedInterviews.noShow,
        headerClassName: styles.kanbanColumnHeaderRejected, // Grey header
        emptyState: <EmptyStateCard message="No no-shows recorded" icon={<NoShowIcon />} />,
      },
      {
        id: ATS_CONSTANTS.INTERVIEW_COLUMNS.CANCELLED,
        title: 'Cancelled',
        items: categorizedInterviews.cancelled,
        headerClassName: styles.kanbanColumnHeaderRejected, // Grey header
        emptyState: <EmptyStateCard message="No cancelled interviews" icon={<CancelledIcon />} />,
      },
    ];
  }, [categorizedInterviews]);

  const renderCard = (interview: InterviewItem, columnId: string) => (
    <InterviewCard
      interview={interview}
      onReschedule={onReschedule}
      sectionType={columnId as any}
      isClosed={isClosed}
      onCardClick={onCardClick}
      onUpdateStatus={onUpdateStatus}
      onCancelInterview={onCancelInterview}
      onGenerateOffer={onGenerateOffer}
    />
  );

  return (
    <KanbanBoard
      columns={columns}
      renderCard={renderCard}
      emptyMessage="No interviews in this stage."
      variant="interview"
      data-testid="ats-interview-board"
    />
  );
};

export default InterviewBoard;
